import React, {useState, useEffect} from 'react';
import {
  Grid,
  Card,
  IconButton,
  Tooltip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Chip,
  Avatar,
  Alert,
  Snackbar,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Restore,
  Visibility,
  Compare,
  Close,
  Check,
  Schedule,
  Person,
  ExpandMore,
  Search,
  DateRange,
  Info,
  Warning,
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {useParams, useNavigate} from 'react-router-dom';
import {Axios} from 'services/axios';
import {Block, Confirm} from 'notiflix';
import moment from 'moment';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';

function PageVersions() {
  const {pageId} = useParams();
  const navigate = useNavigate();

  const [page, setPage] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compareDialog, setCompareDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [compareVersion, setCompareVersion] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [restoreReason, setRestoreReason] = useState('');
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState(null);

  useEffect(() => {
    fetchPageVersions();
  }, [pageId]);

  useEffect(() => {
    if (loading) {
      Block.circle('#versions-container', 'Loading Versions...');
    } else {
      Block.remove('#versions-container');
    }
  }, [loading]);

  const fetchPageVersions = async () => {
    try {
      setLoading(true);

      // Fetch page details
      const pageRes = await Axios.fetch(`pages/${pageId}`);
      setPage(pageRes.data.data.page);

      // Fetch versions
      const versionsRes = await Axios.fetch(`pages/${pageId}/versions`, {
        params: {limit: 50},
      });
      setVersions(versionsRes.data.data.versions || []);
    } catch (error) {
      console.error('Error fetching versions:', error);
      showSnackbar('Error loading versions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({open: true, message, severity});
  };

  // ========== FILTER VERSIONS ==========
  const filteredVersions = versions.filter(version => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      version.versionNumber.toString().includes(searchLower) ||
      version.changeSummary?.toLowerCase().includes(searchLower) ||
      version.createdBy?.toLowerCase().includes(searchLower) ||
      moment(version.createdAt)
        .format('DD MMM YYYY')
        .toLowerCase()
        .includes(searchLower)
    );
  });

  // ========== RESTORE VERSION ==========
  const handleRestoreClick = version => {
    setVersionToRestore(version);
    setShowRestoreDialog(true);
  };

  const confirmRestore = async () => {
    if (!versionToRestore) return;

    Confirm.show(
      'Restore Version',
      `Are you sure you want to restore to version ${versionToRestore.versionNumber}?`,
      'Yes, Restore',
      'Cancel',
      async () => {
        try {
          Block.circle('#versions-container', 'Restoring...');

          await Axios.post(
            `pages/${pageId}/restore/${versionToRestore.id}`,
            {
              changeSummary:
                restoreReason ||
                `Restored to version ${versionToRestore.versionNumber}`,
            },
          );

          showSnackbar(
            `Page restored to version ${versionToRestore.versionNumber}`,
          );
          setShowRestoreDialog(false);
          setRestoreReason('');
          setVersionToRestore(null);
          fetchPageVersions();
        } catch (error) {
          console.error('Error restoring:', error);
          showSnackbar('Error restoring version', 'error');
        } finally {
          Block.remove('#versions-container');
        }
      },
    );
  };

  // ========== PREVIEW VERSION ==========
  const handlePreview = version => {
    setSelectedVersion(version);
    setPreviewDialog(true);
  };

  // ========== COMPARE VERSIONS ==========
  const handleCompareClick = version => {
    if (!compareVersion) {
      setCompareVersion(version);
      showSnackbar(
        `Version ${version.versionNumber} selected for comparison. Select another version to compare.`,
        'info',
      );
    } else {
      setSelectedVersion(version);
      setCompareDialog(true);
    }
  };

  const clearCompare = () => {
    setCompareVersion(null);
    setSelectedVersion(null);
    setCompareDialog(false);
  };

  // ========== RENDER VERSION DIFF ==========
  const renderDiff = (obj1, obj2, path = '') => {
    const differences = [];

    const allKeys = new Set([
      ...Object.keys(obj1 || {}),
      ...Object.keys(obj2 || {}),
    ]);

    allKeys.forEach(key => {
      const val1 = obj1?.[key];
      const val2 = obj2?.[key];
      const currentPath = path ? `${path}.${key}` : key;

      // Skip if both are undefined/null
      if (val1 === undefined && val2 === undefined) return;

      if (
        typeof val1 === 'object' &&
        typeof val2 === 'object' &&
        val1 !== null &&
        val2 !== null &&
        !Array.isArray(val1)
      ) {
        // Recursively check nested objects
        const nestedDiffs = renderDiff(val1, val2, currentPath);
        differences.push(...nestedDiffs);
      } else if (JSON.stringify(val1) !== JSON.stringify(val2)) {
        differences.push({
          field: currentPath,
          oldValue:
            val1 !== undefined
              ? typeof val1 === 'object'
                ? JSON.stringify(val1)
                : String(val1)
              : '(empty)',
          newValue:
            val2 !== undefined
              ? typeof val2 === 'object'
                ? JSON.stringify(val2)
                : String(val2)
              : '(empty)',
        });
      }
    });

    return differences;
  };

  // ========== RENDER PREVIEW DIALOG ==========
  const renderPreviewDialog = () => (
    <Dialog
      open={previewDialog}
      onClose={() => setPreviewDialog(false)}
      maxWidth="md"
      fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Visibility color="info" />
            <MDTypography variant="h6">
              Version {selectedVersion?.versionNumber} Preview
            </MDTypography>
          </Box>
          <IconButton onClick={() => setPreviewDialog(false)}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {selectedVersion && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper sx={{p: 2, bgcolor: 'grey.50'}}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <MDTypography variant="caption" color="text">
                      Version Number
                    </MDTypography>
                    <MDTypography variant="body2">
                      {selectedVersion.versionNumber}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={6}>
                    <MDTypography variant="caption" color="text">
                      Created
                    </MDTypography>
                    <MDTypography variant="body2">
                      {moment(selectedVersion.createdAt).format(
                        'DD MMM YYYY, h:mm A',
                      )}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={6}>
                    <MDTypography variant="caption" color="text">
                      Created By
                    </MDTypography>
                    <MDTypography variant="body2">
                      {selectedVersion.createdBy || 'System'}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={6}>
                    <MDTypography variant="caption" color="text">
                      Change Summary
                    </MDTypography>
                    <MDTypography variant="body2">
                      {selectedVersion.changeSummary || 'No summary'}
                    </MDTypography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <MDTypography variant="subtitle2">Page Settings</MDTypography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        {Object.entries(selectedVersion.snapshot || {}).map(
                          ([key, value]) => (
                            <TableRow key={key}>
                              <TableCell
                                sx={{fontWeight: 'bold', width: '30%'}}>
                                {key
                                  .replace(/([A-Z])/g, ' $1')
                                  .replace(/^./, str => str.toUpperCase())}
                              </TableCell>
                              <TableCell>
                                {typeof value === 'object' ? (
                                  <pre style={{margin: 0, fontSize: '0.8rem'}}>
                                    {JSON.stringify(value, null, 2)}
                                  </pre>
                                ) : (
                                  String(value)
                                )}
                              </TableCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPreviewDialog(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  // ========== RENDER COMPARE DIALOG ==========
  const renderCompareDialog = () => {
    if (!selectedVersion || !compareVersion) return null;

    const differences = renderDiff(
      compareVersion.snapshot,
      selectedVersion.snapshot,
    );

    return (
      <Dialog
        open={compareDialog}
        onClose={clearCompare}
        maxWidth="lg"
        fullWidth>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Compare color="warning" />
              <MDTypography variant="h6">
                Comparing Version {compareVersion.versionNumber} vs{' '}
                {selectedVersion.versionNumber}
              </MDTypography>
            </Box>
            <IconButton onClick={clearCompare}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Version Info */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper sx={{p: 2, bgcolor: 'info.light', color: 'white'}}>
                    <MDTypography variant="caption" color="white" opacity={0.8}>
                      Version {compareVersion.versionNumber}
                    </MDTypography>
                    <MDTypography variant="body2" color="white">
                      {moment(compareVersion.createdAt).format(
                        'DD MMM YYYY, h:mm A',
                      )}
                    </MDTypography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{p: 2, bgcolor: 'success.light', color: 'white'}}>
                    <MDTypography variant="caption" color="white" opacity={0.8}>
                      Version {selectedVersion.versionNumber}
                    </MDTypography>
                    <MDTypography variant="body2" color="white">
                      {moment(selectedVersion.createdAt).format(
                        'DD MMM YYYY, h:mm A',
                      )}
                    </MDTypography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>

            {/* Differences */}
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <MDTypography variant="subtitle2">
                    Differences Found ({differences.length})
                  </MDTypography>
                </AccordionSummary>
                <AccordionDetails>
                  {differences.length === 0 ? (
                    <Alert severity="info">
                      No differences found between these versions
                    </Alert>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Field</TableCell>
                            <TableCell>
                              Version {compareVersion.versionNumber}
                            </TableCell>
                            <TableCell>
                              Version {selectedVersion.versionNumber}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {differences.map((diff, index) => (
                            <TableRow key={index}>
                              <TableCell sx={{fontWeight: 'bold'}}>
                                {diff.field}
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: 'error.main',
                                  bgcolor: 'error.light',
                                  opacity: 0.9,
                                }}>
                                {diff.oldValue}
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: 'success.main',
                                  bgcolor: 'success.light',
                                  opacity: 0.9,
                                }}>
                                {diff.newValue}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearCompare} variant="outlined">
            Close
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setCompareDialog(false);
              handleRestoreClick(selectedVersion);
            }}>
            Restore to Version {selectedVersion.versionNumber}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ========== RENDER RESTORE DIALOG ==========
  const renderRestoreDialog = () => (
    <Dialog
      open={showRestoreDialog}
      onClose={() => {
        setShowRestoreDialog(false);
        setVersionToRestore(null);
        setRestoreReason('');
      }}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Restore color="success" />
          <MDTypography variant="h6">
            Restore to Version {versionToRestore?.versionNumber}
          </MDTypography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="info">
              <Box>
                <MDTypography variant="body2">
                  <strong>Version {versionToRestore?.versionNumber}</strong>{' '}
                  created on{' '}
                  {moment(versionToRestore?.createdAt).format(
                    'DD MMM YYYY, h:mm A',
                  )}
                </MDTypography>
                {versionToRestore?.changeSummary && (
                  <MDTypography variant="body2" sx={{mt: 1}}>
                    Summary: {versionToRestore.changeSummary}
                  </MDTypography>
                )}
              </Box>
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for Restore (optional)"
              value={restoreReason}
              onChange={e => setRestoreReason(e.target.value)}
              placeholder="Explain why you're restoring to this version..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setShowRestoreDialog(false);
            setVersionToRestore(null);
            setRestoreReason('');
          }}>
          Cancel
        </Button>
        <Button variant="contained" color="success" onClick={confirmRestore}>
          Confirm Restore
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3} id="versions-container">
        <Grid container spacing={3}>
          {/* Header */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info">
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <MDTypography variant="h6" color="white">
                      Page Version History
                    </MDTypography>
                    {page && (
                      <MDTypography variant="body2" color="white" opacity={0.8}>
                        {page.name} ({page.route}) • Current Version:{' '}
                        {page.version || 1}
                      </MDTypography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4} sx={{textAlign: 'right'}}>
                    <MDButton
                      variant="gradient"
                      color="dark"
                      onClick={() => navigate(`/pages/${pageId}/sections`)}>
                      Back to Sections
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Search Bar */}
              <MDBox p={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search versions by number, summary, or creator..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {compareVersion && (
                      <Alert severity="info" sx={{mb: 2}}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center">
                          <span>
                            Version {compareVersion.versionNumber} selected for
                            comparison
                          </span>
                          <IconButton size="small" onClick={clearCompare}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              </MDBox>

              {/* Version Timeline */}
              <MDBox p={3}>
                {filteredVersions.length === 0 ? (
                  <Paper sx={{p: 4, textAlign: 'center'}}>
                    <Info sx={{fontSize: 48, color: 'text.disabled', mb: 2}} />
                    <MDTypography variant="h6" color="text" gutterBottom>
                      {searchTerm
                        ? 'No matching versions found'
                        : 'No version history available'}
                    </MDTypography>
                    {searchTerm && (
                      <MDButton
                        variant="outlined"
                        size="small"
                        onClick={() => setSearchTerm('')}
                        sx={{mt: 2}}>
                        Clear Search
                      </MDButton>
                    )}
                  </Paper>
                ) : (
                  <Timeline position="alternate">
                    {filteredVersions.map((version, index) => (
                      <TimelineItem key={version.id}>
                        <TimelineOppositeContent color="text.secondary">
                          <Box>
                            <MDTypography variant="caption" display="block">
                              {moment(version.createdAt).format('DD MMM YYYY')}
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                              {moment(version.createdAt).format('h:mm A')}
                            </MDTypography>
                          </Box>
                        </TimelineOppositeContent>

                        <TimelineSeparator>
                          <TimelineDot
                            color={version.isCurrent ? 'success' : 'primary'}>
                            {version.isCurrent ? <Check /> : <Schedule />}
                          </TimelineDot>
                          {index < filteredVersions.length - 1 && (
                            <TimelineConnector />
                          )}
                        </TimelineSeparator>

                        <TimelineContent>
                          <Paper
                            elevation={3}
                            sx={{p: 2, position: 'relative'}}>
                            {version.isCurrent && (
                              <Chip
                                label="Current"
                                size="small"
                                color="success"
                                sx={{position: 'absolute', top: -10, right: 10}}
                              />
                            )}

                            <Box>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={1}>
                                <MDTypography variant="body2" fontWeight="bold">
                                  Version {version.versionNumber}
                                </MDTypography>
                              </Box>

                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                mb={1}>
                                <Person fontSize="small" color="action" />
                                <MDTypography variant="caption">
                                  {version.createdBy || 'System'}
                                </MDTypography>
                              </Box>

                              {version.changeSummary && (
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  display="block"
                                  sx={{mb: 2}}>
                                  "{version.changeSummary}"
                                </MDTypography>
                              )}

                              <Divider sx={{my: 1}} />

                              <Box
                                display="flex"
                                justifyContent="flex-end"
                                gap={1}
                                mt={1}>
                                <Tooltip title="Preview">
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => handlePreview(version)}>
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip
                                  title={
                                    compareVersion
                                      ? 'Compare'
                                      : 'Select for comparison'
                                  }>
                                  <IconButton
                                    size="small"
                                    color={
                                      compareVersion?.id === version.id
                                        ? 'warning'
                                        : 'default'
                                    }
                                    onClick={() => handleCompareClick(version)}>
                                    <Compare fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                {!version.isCurrent && (
                                  <Tooltip title="Restore">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() =>
                                        handleRestoreClick(version)
                                      }>
                                      <Restore fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>

                              {/* Compare indicator */}
                              {compareVersion?.id === version.id && (
                                <Alert
                                  severity="warning"
                                  sx={{mt: 2}}
                                  icon={<Compare />}>
                                  Selected for comparison
                                </Alert>
                              )}
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                    ))}
                  </Timeline>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialogs */}
      {renderPreviewDialog()}
      {renderCompareDialog()}
      {renderRestoreDialog()}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
        <Alert
          onClose={() => setSnackbar({...snackbar, open: false})}
          severity={snackbar.severity}
          sx={{width: '100%'}}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default PageVersions;
